// Точка входа embed-бандла (public/widget.js):
//   <script src="https://host/widget.js" data-key="asst_..." async></script>
// data-host переопределяет origin API (по умолчанию — origin самого скрипта).
// visitor_key связывает сообщения посетителя в один диалог между визитами:
// обычно живёт в localStorage, а кабинет платформы даёт его embed-тегом
// (data-visitor-key — ключ аккаунта, см. visitor_key.ts).
import { FormFields, FormPatch, WidgetApi } from './api';
import { visitorKeyFor } from './visitor_key';
import { ChatWidget } from './widget';

declare global {
  interface Window {
    __assiistentWidgetMounted?: boolean;
    /** JS-API хост-страницы:
     *    window.assiistent?.open('текст вопроса')   — открыть чат с готовым вопросом;
     *    window.assiistent?.context([{ label: 'Длина, м', value: 11 }])
     *      — сказать боту, что посетитель уже ввёл в форму сайта, чтобы он не
     *        заставлял диктовать это заново («Помощник формы»);
     *    await window.assiistent?.continueUrl('max')
     *      — диплинк в бота для своей кнопки на странице; null, если канала нет. */
    assiistent?: {
      open: (prefill?: string) => void;
      context: (fields: FormFields) => void;
      on: (event: 'patch', callback: (patch: FormPatch) => void) => () => void;
      continueUrl: (kind: 'max' | 'telegram') => Promise<string | null>;
    };
  }
}

function boot(): void {
  if (window.__assiistentWidgetMounted) return;

  const script = document.currentScript as HTMLScriptElement | null;
  const publicKey = script?.dataset.key;
  if (!script || !publicKey) {
    console.error('[assiistent] в embed-теге не задан data-key');
    return;
  }
  const origin = script.dataset.host || new URL(script.src, window.location.href).origin;
  window.__assiistentWidgetMounted = true;

  const mountWidget = (): void => {
    const api = new WidgetApi(`${origin}/widget/${publicKey}`);
    const widget = new ChatWidget(api, visitorKeyFor(publicKey, script.dataset.visitorKey), {
      publicKey,
      // data-prefill embed-тега: чат открывается с готовым текстом в поле ввода.
      prefill: script.dataset.prefill ?? '',
      // data-launcher="none": без пузыря и приглашений — чат открывает сама
      // страница через window.assiistent.open() (поддержка в кабинете).
      launcher: script.dataset.launcher === 'none' ? 'none' : 'bubble',
    });
    widget.mount();
    window.assiistent = {
      open: (prefill?: string) => widget.openWith(prefill),
      context: (fields: FormFields) => widget.setContext(fields),
      on: (event: 'patch', callback: (patch: FormPatch) => void) => widget.on(event, callback),
      continueUrl: (kind: 'max' | 'telegram') => widget.continueUrl(kind),
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountWidget, { once: true });
  } else {
    mountWidget();
  }
}

boot();
